import { toast } from "sonner";
import { baseApi } from "../api/baseApi";
import {
  WorkItemList,
  WorkItemStateEnum,
  WorkItemTypeEnum,
  WorkItemFavoriteTypeEnum,
  UserWorkItemsParams,
} from "../types/work.types";
import { CalendarEvent } from "../ms-services/calendarApi";

// Re-export types that are used by other modules
export { WorkItemFavoriteTypeEnum } from "../types/work.types";

// Calendar sync types (from workItemCalendarSyncApi.ts)
export interface CalendarSyncRequest {
  added: CalendarEvent[];
  modified: CalendarEvent[];
  deleted: CalendarEvent[];
}

export interface WorkItemSyncResult {
  workItemNo: string;
  calendarEventId: string;
  scheduledTo: string;
  workItem: WorkItemList;
}

export interface WorkItemSyncFailure {
  workItemNo: string;
  calendarEventId: string;
  error: string;
}

export interface CalendarSyncResponse {
  UpdatedWorkItems: WorkItemSyncResult[];
  FailedWorkItems: WorkItemSyncFailure[];
}

export const workItemApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Work item management (from workItemApi.ts)
    getUserWorkItems: builder.query<WorkItemList[], UserWorkItemsParams>({
      query: (params) => {
        const queryParams: Record<string, string> = {};

        if (params?.fromDate) {
          queryParams.fromDate = params.fromDate;
        }
        if (params?.toDate) {
          queryParams.toDate = params.toDate;
        }
        if (params?.state !== undefined) {
          queryParams.state = params.state.toString();
        }
        if (params?.userId !== undefined) {
          queryParams.userId = params.userId.toString();
        }

        return {
          url: `/v2/crm/workitems/by-user`,
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ["WorkItem", "Workitems"],
    }),

    getWorkItem: builder.query<WorkItemList, { workItemId: number }>({
      query: ({ workItemId }) => ({
        url: `/v2/crm/works/workitems/${workItemId}`,
        method: "GET",
      }),
      providesTags: (result, error, { workItemId }) => [
        { type: "WorkItem", id: workItemId },
      ],
    }),

    // Partial update of a work item. Send only the fields you want to change.
    patchWorkItem: builder.mutation<
      WorkItemList,
      {
        workItemId: number;
        patch: {
          Type?: WorkItemTypeEnum;
          Name?: string;
          Description?: string;
          ScheduledTo?: string;
        };
      }
    >({
      query: ({ workItemId, patch }) => ({
        url: `/v2/crm/workitem/${workItemId}/`,
        method: "PATCH",
        body: patch,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          toast.error("Det gick inte att uppdatera aktiviteten.");
        }
      },
      invalidatesTags: (_result, _error, { workItemId }) => [
        { type: "WorkItem", id: workItemId },
        "Workitems",
        "Work",
        "NextAction",
      ],
    }),

    setWorkItemFavorite: builder.mutation<
      WorkItemList,
      { workItemId: number; type: WorkItemFavoriteTypeEnum }
    >({
      query: ({ type, workItemId }) => ({
        url: `/v2/crm/workitems/${workItemId}/toggle-favorite/${type}`,
        method: "POST",
        body: {},
      }),

      async onQueryStarted(
        { workItemId, ...patch },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          workItemApi.util.updateQueryData(
            "getUserWorkItems",
            { state: WorkItemStateEnum.Active },
            (workItems) => {
              const existingWorkItem = workItems.find(
                (item) => item.Id === workItemId
              );

              if (!existingWorkItem) {
                return;
              }
              existingWorkItem.FavoriteType = patch.type;
              existingWorkItem.Modified = new Date().toISOString();
            }
          )
        );
        const patchResult2 = dispatch(
          workItemApi.util.updateQueryData(
            "getWorkItem",
            { workItemId },
            (draft) => {
              const existingWorkItem = draft;

              if (!existingWorkItem) {
                return;
              }
              existingWorkItem.FavoriteType = patch.type;
              existingWorkItem.Modified = new Date().toISOString();
              Object.assign(existingWorkItem, patch);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchResult2.undo();
          toast.error(
            "Det gick inte att uppdatera favorit, försök igen senare."
          );
        }
      },
      invalidatesTags: (result, error, { workItemId }) => [
        { type: "WorkItem", id: workItemId },
        "Workitems",
      ],
    }),

    // Calendar sync endpoints (from workItemCalendarSyncApi.ts)
    updateWorkItemsFromCalendar: builder.mutation<
      CalendarSyncResponse,
      CalendarSyncRequest
    >({
      query: (request) => ({
        url: "/v2/crm/workitems/calendar-sync",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["WorkItem", "Workitems"],
    }),

    getWorkItemCalendarSync: builder.query<WorkItemSyncResult, number>({
      query: (workItemId) => ({
        url: `/v2/crm/workitems/${workItemId}/calendar-sync`,
        method: "GET",
      }),
      providesTags: (result, error, workItemId) => [
        { type: "WorkItem", id: workItemId },
      ],
    }),
  }),
});

// Export hooks
export const {
  // Work item management
  useGetUserWorkItemsQuery,
  useGetWorkItemQuery,
  usePatchWorkItemMutation,
  useSetWorkItemFavoriteMutation,

  // Calendar sync
  useUpdateWorkItemsFromCalendarMutation,
  useGetWorkItemCalendarSyncQuery,
} = workItemApi;
